'use client';

import classNames from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Typography from '@/components/atoms/typography/typography';
import { FilterItem } from '@/components/organisms/filter-card-list/filter-card-list';
import {
  AreaData,
  DateData,
  DurationOptions,
  LocationChangeParameters,
  LocationData,
  PersonsData,
  Room,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import {
  extractAdultCount,
  extractChildrenAges,
  extractDepartureDate,
  extractDurationTertiary,
  extractMaxDepartureDate,
  extractTransportationMethod,
  extractTravelPortalLocationCodes,
  filterCampaignAvailableDates,
  generatePersonsSectionValueString,
  getArea,
  getAreaByValue,
  getAvailablePeriodTypesFromDates,
  getTravelPortalAreaDefaults,
  isPersonsSectionValid,
  TravelPortalAreaDefaults,
} from '@/components/organisms/search-bar/shared/search-utils/search-utils';
import { AdaptCampaignsAvailableDates } from '@/integrations/travelportal/campaigns/available-dates/available-dates.adapter';
import useQueryCampaignsAvailableDates from '@/integrations/travelportal/campaigns/available-dates/available-dates.hooks';
import { AdaptCampaignsDestinationHierarchy } from '@/integrations/travelportal/campaigns/destinations-hierarchy/destination-hierarchy.adapter';
import useQueryCampaignDestinationHierarchy from '@/integrations/travelportal/campaigns/destinations-hierarchy/destination-hierarchy.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  campaignPageEvent$,
  CampaignPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import useUiEventOutsideHandler from '@/libraries/hooks/use-ui-event-outside';
import SearchBarWrapper from './search-bar-wrapper/search-bar-wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './search-bar.module.scss';

export interface SearchDefaults {
  adultsNumber?: string;
  adultsPerRoom?: string;
  childrenAges?: string[];
  childrenAgesPerRoom?: string;
  departureDate?: Date;
  departureId?: string;
  destinationIds?: string[] | null;
  destinationRegionIds?: string[] | null;
  durationType?: Content.Components['PeriodType'] | null;
  maxDepartureDate?: Date;
}

export interface SearchBarCampaignProps {
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** Default values for the search bar */
  propDefaultValues?: SearchDefaults;
  /** The search settings */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Indicates weather the search should trigger a navigation to the search page (used when the search bar is used on a different page)*/
  shouldNavigate?: boolean;
  /** Indicates weather the search should trigger a refresh of the page with the updated url params */
  shouldRefresh?: boolean;
  /** The travel portal search settings */
  travelPortalSearchSettings?:
    | Content.Components['TravelPortalSearchSettingsDto']
    | undefined;
}

export const SearchBarCampaign = forwardRef(
  (
    {
      className,
      shouldNavigate = false,
      shouldRefresh = false,
      travelPortalSearchSettings,
    }: SearchBarCampaignProps,
    ref,
  ) => {
    const { t } = useI18n();
    const { dateFormatter } = useDateFormatter();
    const rootRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const searchParams = useSearchParams();
    const urlDefaultValues = getSearchParameters(searchParams!);
    const pathname = usePathname();
    const prevPath = useRef<string | null>(null);
    const {
      activeSection,
      adultsNo,
      area,
      childrenAges,
      departureIds,
      setActiveSection,
      setAdultsNo,
      setArea,
      setCampaignAvailableDates,
      setChildrenAges,
      setCountryIds,
      setDepartureDate,
      setDepartureIds,
      setDestinationIds,
      setMaxDepartureDate,
      setPeriodType,
      setTriggeredBy,
    } = useSearchContext();
    const locale = getLocale();

    const [travelPortalAreaDefaults, setTravelPortalAreaDefaults] = useState<
      TravelPortalAreaDefaults | undefined
    >(undefined);

    const hasDefaultValue = (parameterName: string): boolean => {
      // Check if parameter is in searchParams
      const isInQuery = searchParams!.has(parameterName);

      // Check if parameter is in searchDefaults
      const isInDefaults = travelPortalSearchSettings
        ? Object.hasOwnProperty.call(travelPortalSearchSettings, parameterName)
        : false;

      return isInQuery || isInDefaults;
    };

    const getDurationOptionsFromSearchSettings = useCallback(
      (selectedDurationType?: string) => {
        const activeDurationType =
          selectedDurationType ||
          urlDefaultValues.durationType ||
          travelPortalAreaDefaults?.defaultPeriodType;

        return (
          travelPortalSearchSettings?.availablePeriodTypes?.map(option => {
            let rangeSize: number;

            // TODO: Handle rangeSize issue (#2311) gracefully
            switch (option.value) {
              case 'week':
                rangeSize = 7;
                break;

              case 'twoWeeks':
                rangeSize = 14;
                break;

              case 'threeWeeks':
                rangeSize = 21;
                break;

              default: {
                rangeSize = 0;
              }
            }

            return {
              headline: option.label,
              icon: 'Calendar' as IconName,
              id: option.value,
              isSelected: option.value === activeDurationType,
              rangeSize,
              text: option.description,
            };
          }) ?? []
        );
      },
      [
        travelPortalAreaDefaults?.defaultPeriodType,
        travelPortalSearchSettings?.availablePeriodTypes,
        urlDefaultValues.durationType,
      ],
    );

    const [areaSection, setAreaSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          areas: travelPortalSearchSettings?.availableAreas,
        } as AreaData,
        id: 0,
        label: '',
        type: SearchBarWrapperSectionType.AREA,
      },
    );

    const [locationSection, setLocationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          locations: [],
          transportationOptions: [],
        },
        id: 1,
        isLoading:
          hasDefaultValue('destinationIds') || hasDefaultValue('departureIds'),
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.location.locationTitle'),
        placeholder: t('items.campaign.searchBar.location.locationPlaceholder'),
        type: SearchBarWrapperSectionType.LOCATIONS,
      });

    const adultsNumber =
      travelPortalAreaDefaults?.defaultPaxDistribution?.adultsNo ?? 2;

    const initialRoomsState: RoomsState = {
      automaticRoomAssignment:
        !!travelPortalSearchSettings?.automaticRoomAssignment,
      rooms: [
        {
          childrenAges:
            travelPortalAreaDefaults?.defaultPaxDistribution?.childrenAges ??
            [],
          numberOfAdults:
            travelPortalAreaDefaults?.defaultPaxDistribution?.adultsNo ?? 2,
          numberOfChildren:
            travelPortalAreaDefaults?.defaultPaxDistribution?.childrenNo ?? 0,
        },
      ],
    };

    const [personsSection, setPersonsSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          adultThreshold: travelPortalSearchSettings?.adultAgeThreshold,
          defaultAdultNumber: adultsNumber,
          maxPersons: travelPortalSearchSettings?.maxNumberOfAdults,
          roomsState: initialRoomsState,
        },
        id: 2,
        isLoading:
          hasDefaultValue('adultsNumber') || hasDefaultValue('childrenAges'),
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.persons.personsTitle'),
        placeholder: t('items.campaign.searchBar.persons.personsPlaceholder'),
        type: SearchBarWrapperSectionType.PERSONS,
        value: generatePersonsSectionValueString({
          roomsState: initialRoomsState,
          t,
        }),
      });

    const initialDurationOptions = getDurationOptionsFromSearchSettings();

    const [dateSection, setDateSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          availableDates: [],
          durationOptions: initialDurationOptions,
        },
        id: 3,
        isLoading: false,
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.date.dateTitle'),
        placeholder: t('items.campaign.searchBar.date.datePlaceholder'),
        type: SearchBarWrapperSectionType.DATE,
      },
    );

    const generateLocationValue = ({
      locations,
      transportationOptions,
    }: {
      locations?: LocationData['locations'];
      transportationOptions?: FilterItem[];
    }): string[] => {
      const activeTransports =
        transportationOptions
          ?.filter(option => option.isSelected && option.headline)
          .map(option => option.headline) || [];

      const activeLocations =
        locations
          ?.flatMap(group =>
            group.options.filter(
              option => option.checked && !option.disabled && option.label,
            ),
          )
          .map(option => option.label) || [];

      const hasTransports = activeTransports.length > 0;
      const hasLocations = activeLocations.length > 0;

      if (hasTransports) {
        const transportsString = activeTransports.join(', ');

        if (hasLocations) {
          return [
            `${transportsString} ${t('items.common.words.to')}`,
            ...(activeLocations as string[]),
          ];
        }

        return [
          `${transportsString} ${t('items.search.searchBar.location.toAllDestinations')}`,
        ];
      }

      return activeLocations as string[];
    };

    const getDuration = useCallback(() => {
      return extractDurationTertiary(dateSection);
    }, [dateSection]);

    const getTransportationMethod = useCallback(() => {
      return extractTransportationMethod(locationSection);
    }, [locationSection]);

    const getCheckedLocationCodes = useCallback(() => {
      return extractTravelPortalLocationCodes(locationSection);
    }, [locationSection]);

    const getAdultCount = useCallback(() => {
      return extractAdultCount(personsSection);
    }, [personsSection]);

    const getChildrenAges = useCallback(() => {
      return extractChildrenAges(personsSection);
    }, [personsSection]);

    const getDepartureDate = useCallback(() => {
      return extractDepartureDate(dateSection);
    }, [dateSection]);

    const getMaxDepartureDate = useCallback(() => {
      return extractMaxDepartureDate(dateSection);
    }, [dateSection]);

    const {
      data: dataDestinationHierarchy,
      isFetching: isFetchingDestinationHierarchy,
    } = useQueryCampaignDestinationHierarchy({
      area: getArea({ areaId: area, travelPortalSearchSettings }) ?? '',
      campaignId: travelPortalSearchSettings?.campaignId ?? null,
      campaignType: travelPortalSearchSettings?.campaignType ?? '',
    });

    const { data: dataAvailableDates, isFetching: isFetchingAvailableDates } =
      useQueryCampaignsAvailableDates({
        adultsNumber: getAdultCount()?.toString(),
        area: getArea({ areaId: area, travelPortalSearchSettings }) ?? '',
        campaignId: travelPortalSearchSettings?.campaignId ?? null,
        campaignType: travelPortalSearchSettings?.campaignType ?? '',
        childrenAges: getChildrenAges(),
        countryIds: getCheckedLocationCodes().countryIds || undefined,
        departureIds: getTransportationMethod(),
        destinationIds: getCheckedLocationCodes().destinationIds || undefined,
      });

    const updateDestinationsAndDepartures = (
      data: AdaptCampaignsDestinationHierarchy,
    ) => {
      setLocationSection(previousSection => {
        // Departures
        const selectedDepartureId =
          travelPortalAreaDefaults?.defaultDepartureCode as string;

        const updatedTransportationOptions = data.departures.map(departure => ({
          ...departure,
          isSelected: departure.id === selectedDepartureId,
        }));

        // Destinations
        // Pre-select destinations from URL params
        const urlCountryIds = urlDefaultValues.countryIds ?? [];
        const urlDestinationIds = urlDefaultValues.destinationIds ?? [];

        // Disable locations that are not available for selected transportation method
        const updatedLocations = data.destinations?.map(group => ({
          ...group,
          options: group.options.map(destination => {
            const isAvailableForDeparture = selectedDepartureId
              ? destination.departureCodes?.includes(selectedDepartureId)
              : true;

            const isSelectedFromUrl =
              urlDestinationIds.includes(destination.id) ||
              urlCountryIds.includes(group.countryCode ?? '');

            return {
              ...destination,
              checked: isAvailableForDeparture ? isSelectedFromUrl : false,
              disabled: !isAvailableForDeparture,
            };
          }),
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...previousSection.dropdownData,
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          } as LocationData,
          isLoading: false,
          value: generateLocationValue({
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          }),
        };
      });
    };

    useEffect(() => {
      if (!dataDestinationHierarchy) return;

      updateDestinationsAndDepartures(dataDestinationHierarchy);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dataDestinationHierarchy,
      travelPortalAreaDefaults?.defaultDepartureCode,
    ]);

    const handleClickOutside = () => {
      setActiveSection(-1);
    };

    useUiEventOutsideHandler(rootRef, handleClickOutside);

    const handleClickSection = (sectionId: number) => {
      if (activeSection === sectionId) {
        setActiveSection(-1);
      } else {
        setActiveSection(sectionId);
      }
    };

    const handleClickClose = () => {
      setActiveSection(-1);
    };

    const handleClickNext = () => {
      setActiveSection(activeSection + 1);
    };

    const handleClickBack = () => {
      setActiveSection(activeSection - 1);
    };

    const handleChangeDuration = (durationId: string) => {
      let updatedOptions: DurationOptions[];

      setDateSection(previousSection => {
        updatedOptions = (
          (previousSection?.dropdownData as DateData)?.durationOptions ?? []
        ).map(option => ({
          ...option,
          isSelected: option.id === durationId,
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            durationOptions: updatedOptions,
          },
        };
      });

      // Filter campaign available dates based on selected duration
      if (!dataAvailableDates?.result) return;

      setCampaignAvailableDates(
        filterCampaignAvailableDates({
          availableDatesData: dataAvailableDates,
          durationId: durationId as TravelPortal.Properties.PeriodType,
          selectedDepartureCode: getTransportationMethod(),
        }),
      );
    };

    const handleChangeTransportation = (transportationId: string) => {
      setLocationSection(previousSection => {
        const dropdownData = previousSection.dropdownData as LocationData;
        const selectedTransportationId = transportationId;
        const updatedTransportationOptions =
          dropdownData.transportationOptions.map(option => ({
            ...option,
            isSelected: option.id === selectedTransportationId,
          }));

        // Filter locations based on transportation method
        const updatedLocations = dropdownData.locations?.map(group => ({
          ...group,
          options: group.options.map(location => {
            const isAvailableForDeparture = location.departureCodes?.includes(
              selectedTransportationId,
            );

            return {
              ...location,
              checked: isAvailableForDeparture ? location.checked : false,
              disabled: !isAvailableForDeparture,
            };
          }),
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as LocationData),
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          },
          value: generateLocationValue({
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          }),
        };
      });
    };

    const handleChangeLocations = useCallback(
      ({
        destinationRegions,
        destinations,
        locations,
      }: LocationChangeParameters) => {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              destinationRegionIds: destinationRegions,
              destinations,
              locations,
            },
            isNotified: false,
            value: generateLocationValue({
              locations,
              transportationOptions: (
                previousSection.dropdownData as LocationData
              ).transportationOptions,
            }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangePersons = (roomsState: RoomsState) => {
      const valueString = generatePersonsSectionValueString({ roomsState, t });

      setPersonsSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as PersonsData),
            roomsState: roomsState,
          },
          isLoading: false,
          value: valueString,
        };
      });
    };

    const handleChangeDate = (newDates: Date[]) => {
      let valueString = '';

      if (newDates.length === 2) {
        const formattedStartDate = dateFormatter(newDates[0]).format(
          'D. MMM YYYY',
        );
        const formattedEndDate = dateFormatter(newDates[1]).format(
          'D. MMM YYYY',
        );

        valueString = `${formattedStartDate} ${t('items.search.searchBar.date.to')} ${formattedEndDate}`;
      }

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            selectedDate: newDates[0],
            selectedMaxDate: newDates[1],
          },
          isLoading: false,
          isNotified: false,
          value: valueString,
        };
      });
    };

    const updateDepartureInLocationSection = (transportationId: string) => {
      setLocationSection(previousSection => {
        const dropdownData = previousSection.dropdownData as LocationData;

        const updatedTransportationOptions =
          dropdownData.transportationOptions.map(option => ({
            ...option,
            isSelected: option.id === transportationId,
          }));

        // Disable locations that are not available for selected transportation method
        const updatedLocations = dropdownData?.locations?.map(group => ({
          ...group,
          options: group.options.map(destination => {
            const isAvailableForDeparture =
              destination.departureCodes?.includes(transportationId);

            return {
              ...destination,
              checked: isAvailableForDeparture ? destination.checked : false,
              disabled: !isAvailableForDeparture,
            };
          }),
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as LocationData),
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          } as LocationData,
          value: generateLocationValue({
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          }),
        };
      });
    };

    const updateSearchBarStateFromUrl = () => {
      const areaFromUrl = urlDefaultValues.area;
      const areaIdFromUrl = areaFromUrl
        ? getAreaByValue({ travelPortalSearchSettings, value: areaFromUrl })
        : '';

      const values = {
        adultsNumber: adultsNo || adultsNumber?.toString() || '',
        area: areaIdFromUrl || area || '',
        childrenAges: childrenAges || urlDefaultValues.childrenAges?.join(','),
        departureDate: urlDefaultValues.departureDate,
        departureId: departureIds || urlDefaultValues.departureId,
        durationRange: urlDefaultValues.durationRange || '',
        maxDepartureDate: urlDefaultValues.maxDepartureDate,
      };

      if (values.area) {
        setArea(values.area);
      }

      const rooms = [
        {
          childrenAges: [],
          numberOfAdults: adultsNo || adultsNumber || 0,
          numberOfChildren: 0,
        },
      ] as Room[];

      if (values.adultsNumber) {
        rooms[0].numberOfAdults = parseInt(values.adultsNumber, 10);
      }

      if (values.childrenAges) {
        rooms[0].childrenAges = values.childrenAges
          .split(',')
          .map((age: string) => parseInt(age, 10));
        rooms[0].numberOfChildren = rooms[0].childrenAges.length;
      }

      if (values.departureId) {
        updateDepartureInLocationSection(values.departureId);
      }

      if (values.departureDate && values.maxDepartureDate) {
        handleChangeDate([values.departureDate, values.maxDepartureDate]);
      }

      handleChangePersons({
        automaticRoomAssignment:
          !!travelPortalSearchSettings?.automaticRoomAssignment,
        rooms,
      });
    };

    const handleClickSearch = () => {
      campaignPageEvent$.next({ type: CampaignPageEventType.SEARCH });

      const constructAndUpdateSearchUrl = () => {
        const adultsNumber = getAdultCount()?.toString() as string;
        const childrenAges = getChildrenAges() as string;
        const departureDate = getDepartureDate() as string;
        const departureIds = getTransportationMethod() as string;
        const { countryIds, destinationIds } = getCheckedLocationCodes();
        const durationType = getDuration() as string;
        const maxDepartureDate = getMaxDepartureDate() as string;

        const parameters = new URLSearchParams({
          area: getArea({ areaId: area, travelPortalSearchSettings }) ?? '',
          batchSize: travelPortalSearchSettings?.batchSize.toString() ?? '10',
          durationType,
          locale: getCulture(locale),
          sortBy:
            travelPortalSearchSettings?.selectedSortBy ?? 'websitePriority',
        });

        if (departureDate !== undefined && maxDepartureDate !== undefined) {
          parameters.append('departureDate', departureDate);
          parameters.append('maxDepartureDate', maxDepartureDate);
        }

        if (departureIds !== undefined) {
          parameters.append('departureIds', departureIds);
        }

        if (countryIds !== undefined && countryIds !== '') {
          parameters.append('countryIds', countryIds);
        }

        if (destinationIds !== undefined && destinationIds !== '') {
          parameters.append('destinationIds', destinationIds);
        }

        if (childrenAges !== undefined) {
          parameters.append('childrenAges', childrenAges);
        }

        if (adultsNumber !== undefined) {
          parameters.append('adultsNumber', adultsNumber);
        }

        const parametersObject: Record<string, any> = {};

        parameters.forEach((value, key) => {
          parametersObject[key] = value;
        });

        setTriggeredBy('searchButton');

        updateUrlWithParameters({
          newParameters: parametersObject,
          overwrite: true,
          redirectUrl: travelPortalSearchSettings?.searchPageUrl,
          router,
          shouldNavigate,
          shouldRefresh,
        });
      };

      if (
        getArea({ areaId: area, travelPortalSearchSettings }) === undefined ||
        getDuration() === undefined ||
        !isPersonsSectionValid(personsSection)
      ) {
        return;
      }

      setActiveSection(-1);
      constructAndUpdateSearchUrl();
    };

    useImperativeHandle(ref, () => ({
      search: handleClickSearch,
    }));

    useEffect(() => {
      updateSearchBarStateFromUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
      const { countryIds, destinationIds } = getCheckedLocationCodes();

      setDepartureIds(getTransportationMethod() as string);
      setDestinationIds(destinationIds);
      setCountryIds(countryIds);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSection]);

    useEffect(() => {
      const childrenAges = getChildrenAges() as string;
      const adultsNumber = getAdultCount()?.toString() as string;

      setChildrenAges(childrenAges);
      setAdultsNo(adultsNumber);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personsSection]);

    useEffect(() => {
      setDepartureDate(getDepartureDate() as string);
      setMaxDepartureDate(getMaxDepartureDate() as string);
      setPeriodType(getDuration() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateSection]);

    const setAreaSectionData = useCallback((): string | undefined => {
      if (
        travelPortalSearchSettings &&
        travelPortalSearchSettings?.availableAreas.length > 0
      ) {
        setAreaSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              areas: travelPortalSearchSettings.availableAreas,
            } as AreaData,
          };
        });
        1;

        // Keep the area from the URL (e.g. when navigating back), otherwise default to the first area
        const areaFromUrl = searchParams?.get('area');
        const areaId =
          (areaFromUrl &&
            getAreaByValue({
              travelPortalSearchSettings,
              value: areaFromUrl,
            })) ||
          travelPortalSearchSettings.availableAreas[0].id;

        setArea(areaId);

        return getArea({ areaId, travelPortalSearchSettings }) ?? '';
      }

      return undefined;
    }, [searchParams, setArea, travelPortalSearchSettings]);

    useEffect(() => {
      if (!area) return;

      setTravelPortalAreaDefaults(
        getTravelPortalAreaDefaults(travelPortalSearchSettings, area),
      );

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area]);

    // Validate selected dates against available dates range from travel portal area defaults and reset if out of range
    useEffect(() => {
      if (!travelPortalAreaDefaults) return;

      const minDate = travelPortalAreaDefaults.minDepartureDate
        ? new Date(travelPortalAreaDefaults.minDepartureDate)
        : undefined;
      const maxDate = travelPortalAreaDefaults.maxDepartureDate
        ? new Date(travelPortalAreaDefaults.maxDepartureDate)
        : undefined;

      const isDateOutOfRange = (date?: Date) => {
        if (!date) return false;

        if (minDate && date < minDate) return true;

        if (maxDate && date > maxDate) return true;

        return false;
      };

      setDateSection(previousSection => {
        const dateData = previousSection.dropdownData as DateData;
        const hasInvalidSelection =
          isDateOutOfRange(dateData.selectedDate) ||
          isDateOutOfRange(dateData.selectedMaxDate);

        return {
          ...previousSection,
          dropdownData: {
            ...dateData,
            maxDate,
            minDate,
            rangeSelect: travelPortalAreaDefaults.rangeSelect ?? true,
            selectedDate: hasInvalidSelection
              ? undefined
              : dateData.selectedDate,
            selectedMaxDate: hasInvalidSelection
              ? undefined
              : dateData.selectedMaxDate,
          },
          isNotified: hasInvalidSelection ? false : previousSection.isNotified,
          value: hasInvalidSelection ? '' : previousSection.value,
        };
      });
    }, [travelPortalAreaDefaults]);

    useEffect(() => {
      if (!dataAvailableDates?.result) return;

      const updateDatesInSections = (data: AdaptCampaignsAvailableDates) => {
        setDateSection(previousSection => {
          const availablePeriodTypes = getAvailablePeriodTypesFromDates(data);

          const currentDuration =
            (previousSection.dropdownData as DateData).durationOptions?.find(
              option => option.isSelected,
            )?.id ??
            getDuration() ??
            travelPortalAreaDefaults?.defaultPeriodType;
          const durationOptionsFromSearchSettings =
            getDurationOptionsFromSearchSettings(currentDuration as string);
          const filteredDurationOptions =
            durationOptionsFromSearchSettings.filter(
              option =>
                !!option.id &&
                availablePeriodTypes.includes(
                  option.id as TravelPortal.Properties.PeriodType,
                ),
            );
          const selectedDurationId =
            filteredDurationOptions.find(option => option.isSelected)?.id ||
            filteredDurationOptions[0]?.id;

          const durationOptions = filteredDurationOptions.map(option => ({
            ...option,
            isSelected: option.id === selectedDurationId,
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as DateData),
              durationOptions,
              maxDate: new Date(
                travelPortalAreaDefaults?.maxDepartureDate as string,
              ),
              minDate: new Date(
                travelPortalAreaDefaults?.minDepartureDate as string,
              ),
              rangeSelect: travelPortalAreaDefaults?.rangeSelect ?? true,
            },
            isLoading: false,
          };
        });
      };

      updateDatesInSections(dataAvailableDates);

      // filter campaign available dates based on selected duration
      if (!travelPortalAreaDefaults || !dataAvailableDates?.result) return;

      setCampaignAvailableDates(
        filterCampaignAvailableDates({
          availableDatesData: dataAvailableDates,
          durationId:
            travelPortalAreaDefaults.defaultPeriodType as TravelPortal.Properties.PeriodType,
          selectedDepartureCode: getTransportationMethod(),
        }),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAvailableDates]);

    // Automatic trigger search
    useEffect(() => {
      const handleAutomaticSearchTrigger = () => {
        const constructAndUpdateSearchUrl = () => {
          const areaParam = setAreaSectionData();

          const parameters = new URLSearchParams({
            area: areaParam ?? '',
            batchSize: travelPortalSearchSettings?.batchSize.toString() ?? '10',
            locale: getCulture(locale),
            sortBy:
              travelPortalSearchSettings?.selectedSortBy ?? 'websitePriority',
          });

          const parametersObject: Record<string, any> = {};

          parameters.forEach((value, key) => {
            parametersObject[key] = value;
          });

          setTriggeredBy('automaticTrigger');

          updateUrlWithParameters({
            newParameters: parametersObject,
            overwrite: false,
            router,
          });
        };

        setActiveSection(-1);
        constructAndUpdateSearchUrl();
      };

      const currentPath = (pathname ?? '').split('?')[0];

      if (prevPath.current !== currentPath) {
        prevPath.current = currentPath;
        handleAutomaticSearchTrigger();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const sections = useMemo(
      () => [areaSection, locationSection, personsSection, dateSection],
      [areaSection, locationSection, personsSection, dateSection],
    );

    return (
      <div
        ref={rootRef}
        className={classNames(styles.component, styles.isCampaign, className)}
      >
        <div className={styles.searchBarLabel}>
          <Typography tag="label" tagStyle="labelMedium">
            {t('items.campaign.searchBar.searchBarLabel')}
          </Typography>
        </div>

        <SearchBarWrapper
          activeSection={activeSection}
          areaSection={areaSection}
          buttonLabel={t('items.campaign.searchBar.searchBarButton')}
          canSubmit
          compact={true}
          isLoadingDate={isFetchingAvailableDates}
          isLoadingLocations={isFetchingDestinationHierarchy}
          onChangeDate={handleChangeDate}
          onChangeDuration={handleChangeDuration}
          onChangeLocations={handleChangeLocations}
          onChangePersons={handleChangePersons}
          onChangeTransportation={handleChangeTransportation}
          onClickBack={handleClickBack}
          onClickClose={handleClickClose}
          onClickNext={handleClickNext}
          onClickSearch={handleClickSearch}
          onClickSection={handleClickSection}
          searchSettings={travelPortalSearchSettings}
          sections={sections}
        />
      </div>
    );
  },
);

SearchBarCampaign.displayName = 'SearchBarCampaign';
